import apiSlice from "../api/apiSlice";

export const sessionsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* GET */
    getSessionsByCommunityId: builder.query({
      query: (id) => ({
        url: `/communities/${id}/sessions`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
    }),
    getSessionsByCommunityIdPagination: builder.query({
      query: ({
        communityId,
        page,
        limit,
        date,
        time,
        status,
        message,
        sortColumn,
        sortDirection,
      }) => ({
        url:
          `/communities/${communityId}/sessions/pagination?` +
          `limit=${limit}&` +
          `offset=${page * limit}` +
          `${date ? `&date=${date}` : ""}` +
          `${time ? `&time=${time}` : ""}` +
          `${status ? `&status=${status}` : ""}` +
          `${message ? `&message=${message}` : ""}` +
          `${sortDirection ? `&sortDirection=${sortDirection}` : ""}` +
          `${sortColumn ? `&sortColumn=${sortColumn}` : ""}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
    }),
    getTransactionAdjustmentsByCommunityIdPagination: builder.query({
      query: ({ limit, page, date, time, resourceId, communityId, sortColumn, sortDirection }) => ({
        url:
          `/communities/${communityId}/market/transaction_adjustments/pagination?` +
          `limit=${limit}&` +
          `offset=${page * limit}` +
          `${date ? `&date=${date}` : ""}` +
          `${time ? `&time=${time}` : ""}` +
          `${resourceId ? `&resource_id=${resourceId}` : ""}` +
          `${sortDirection ? `&sort_direction=${sortDirection}` : ""}` +
          `${sortColumn ? `&sort_column=${sortColumn}` : ""}`,
        method: "GET",
      }),
      providesTags: () => [{ type: "TransactionAdjustments", id: "LIST" }],
    }),
    getSessionById: builder.query({
      query: ({ communityId, sessionId, excludeDeleted }) => ({
        url:
          `/communities/${communityId}/sessions/${sessionId}/market?` +
          `${excludeDeleted !== undefined ? `&exclude_deleted=${excludeDeleted}` : ""}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
    }),
    getSessionInfoById: builder.query({
      query: ({ communityId, sessionId }) => ({
        url: `/communities/${communityId}/sessions/${sessionId}/info`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
    }),
    getEnergySharedByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/sessions/metrics/energy-shared/community?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&` +
          `granularity=${granularity}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "EnergyShared-Community" }],
    }),
    getMarketKpisByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate }) => ({
        url:
          `/communities/${communityId}/sessions/metrics/kpis/market?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Kpis-Market" }],
    }),
    getMarketMetricsByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/sessions/metrics/all/market?` +
          `granularity=${granularity}&` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "General-Market" }],
    }),
    getMarketMemberMetricsByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate }) => ({
        url:
          `/communities/${communityId}/sessions/metrics/market/member?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Meter-Market" }],
    }),
    getSettlementBySessionId: builder.query({
      query: ({ communityId, sessionId, excludeDeleted }) => ({
        url:
          `/communities/${communityId}/sessions/${sessionId}/settlement?` +
          `${excludeDeleted !== undefined ? `&exclude_deleted=${excludeDeleted}` : ""}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
    }),
    getSavingsByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/sessions/metrics/savings/community?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&` +
          `granularity=${granularity}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Savings-Community" }],
    }),
    getResourcesTotalRevenueByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate }) => ({
        url:
          `/communities/${communityId}/sessions/metrics/revenue/resources?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "TotalRevenue-Resources" }],
    }),
    getSettlementMemberMetricsByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/sessions/metrics/member/settlement?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&` +
          `granularity=${granularity}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Settlement-Member" }],
    }),
    getSettlementMemberResourceMetricsByCommunityId: builder.query({
      query: ({ communityId, startDate, endDate, granularity }) => ({
        url:
          `/communities/${communityId}/sessions/metrics/member-resource/settlement?` +
          `start_date=${startDate}&` +
          `end_date=${endDate}&` +
          `granularity=${granularity}`,
        method: "GET",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
      providesTags: [{ type: "Metrics", id: "Settlement-MemberResource" }],
    }),
    deleteSessionsBetweenDates: builder.mutation({
      query: ({ communityId, startDate, endDate }) => ({
        url:
          `/communities/${communityId}/sessions/between_dates?` +
          `start_date=${startDate}&end_date=${endDate}`,
        method: "DELETE",
        validateStatus: (response, result) => response.status === 200 && !result?.isError,
      }),
    }),
  }),
});

export const {
  useGetSessionsByCommunityIdQuery,
  useGetSessionsByCommunityIdPaginationQuery,
  useGetSessionByIdQuery,
  useGetSessionInfoByIdQuery,
  useGetEnergySharedByCommunityIdQuery,
  useGetMarketKpisByCommunityIdQuery,
  useGetMarketMetricsByCommunityIdQuery,
  useGetMarketMemberMetricsByCommunityIdQuery,
  useGetSettlementBySessionIdQuery,
  useGetSavingsByCommunityIdQuery,
  useGetResourcesTotalRevenueByCommunityIdQuery,
  useGetSettlementMemberMetricsByCommunityIdQuery,
  useGetSettlementMemberResourceMetricsByCommunityIdQuery,
  useGetTransactionAdjustmentsByCommunityIdPaginationQuery,
  useDeleteSessionsBetweenDatesMutation,
} = sessionsApiSlice;
